import React from "react";
import styled from "styled-components";
import { Paper } from "@mui/material";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import LabelBottomNavigation from "../BottomNavigation";
import NavbarQuizComp from "./NavbarQuizComp";
import { auth } from "../../../services/firebase.config";
import { Black } from "../../theme/colors";

export const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.$background};
  padding-bottom: 50px;
`;

export const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export const Body = styled.div`
  margin-bottom: 100px;
  margin-top: ${(props) =>
    props.$hasQuizHeader
      ? "200px"
      : "140px"}; /* Adjust based on navbar height */
  overflow-x: hidden;
`;

export const Section = styled.div`
  min-height: 25vh;
  padding: 50px 10px 50px;
`;

export const StickyFooter = styled(Paper)`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  z-index: 10; /* Ensure it stays above other elements */
`;

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const lastHandledDeepLink = { current: null };

// Reset function for quiz layout deep link state
const resetQuizDeepLinkState = () => {
  lastHandledDeepLink.current = null;
  console.log("🔄 Quiz deep link state reset");
};

// Add to global reset if it exists
if (window.resetDeepLinkState) {
  const originalReset = window.resetDeepLinkState;
  window.resetDeepLinkState = () => {
    originalReset();
    resetQuizDeepLinkState();
  };
} else {
  window.resetDeepLinkState = resetQuizDeepLinkState;
}

function LayoutQuiz(props) {
  const currentUser = auth.currentUser;

  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleDeepLink = ({ url }) => {
      // Only block if it's the exact same URL (prevents double-handling)
      if (lastHandledDeepLink.current === url) {
        console.log("🚫 Ignoring duplicate deep link:", url);
        return;
      }

      console.log("🔥 appUrlOpen triggered with:", url);

      // Ignore Firebase auth verification deep links - they shouldn't trigger navigation
      if (
        url.includes("firebaseauth/link") ||
        url.includes("authType=verifyApp")
      ) {
        console.log("🚫 Ignoring Firebase auth verification deep link:", url);
        return;
      }

      try {
        // Handle both custom scheme (vibe-check://) and universal links (https://)
        let path;
        if (url.startsWith("vibe-check://")) {
          const fullPath = url.replace("vibe-check://", "");
          const [pathPart, query = ""] = fullPath.split("?");
          path = `/${pathPart}${query ? `?${query}` : ""}`;
        } else {
          // Universal link - parse URL properly
          const urlObj = new URL(url);
          path = urlObj.pathname + urlObj.search;
        }

        // Handle /s/ and /p/ prefixed routes (server-side meta routes)
        if (path.startsWith("/s/")) {
          const parts = path.split("?");
          const pathPart = parts[0];
          const query = parts[1] ? `?${parts[1]}` : "";
          const segments = pathPart.split("/").filter(Boolean);

          if (segments.length >= 2) {
            const quizType = segments[1];
            // Legacy quizzes use /{quizType}, new dynamic quizzes use /quiz/{quizType}
            const legacyQuizTypes = new Set([
              "best-date",
              "personality-check",
              "political-alignment",
              "sex-styles",
              "attachment-styles",
              "am-i-gay",
              "affection-profile",
              "love-languages",
            ]);
            const isLegacyQuiz = legacyQuizTypes.has(quizType);
            path = isLegacyQuiz
              ? `/${quizType}${query}`
              : `/quiz/${quizType}${query}`;
          }
        } else if (path.startsWith("/p/")) {
          path = path.replace("/p/", "/");
        }

        setTimeout(() => {
          console.log("📍 Navigating to:", path);
          navigate(path);
        }, 500);

        lastHandledDeepLink.current = url; // Track this URL to prevent duplicates
      } catch (err) {
        console.warn("❌ Could not parse deep link:", url);
      }
    };

    CapacitorApp.addListener("appUrlOpen", handleDeepLink);

    CapacitorApp.getLaunchUrl().then((res) => {
      if (res?.url) {
        console.log("🚀 Cold start URL:", res.url);
        handleDeepLink(res);
      }
    });

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [navigate]);

  if (!isMobile) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "black",
          color: "white",
          fontFamily: "Satoshi-Variable",
          textAlign: "center",
          padding: "20px",
        }}
      >
        This app is currently only available on mobile devices. Please open it
        on your phone.
      </div>
    );
  }

  return (
    <Wrapper $background={props.$background || Black}>
      <NavbarQuizComp quizHeader={props.quizHeader} />
      <StyledLayout>
        <Body $hasQuizHeader={!!props.quizHeader}>{props.children}</Body>
      </StyledLayout>
      {currentUser && !currentUser.isAnonymous && (
        <StickyFooter sx={{ position: "fixed" }} elevation={3}>
          <LabelBottomNavigation />
        </StickyFooter>
      )}
    </Wrapper>
  );
}

export default LayoutQuiz;
